// Géométrie de l'axe « plancher -> cible » d'un compte.
//
// Extrait du rendu pour être TESTABLE : une erreur de proportion sur un axe
// de drawdown ne se voit pas à l'œil, et le composant affirme une marge de
// sécurité — une borne fausse est pire que pas d'axe du tout.
//
// ⚠️ LE PIÈGE : `BufferStatus.floorAmount` n'est écrit QUE par la branche
// trailing de `computeBufferStatus` (src/trading-accounts/propFirmEngine.ts:422
// et 440). Les branches `total_loss` et `daily_loss` retournent sans. On
// dérive donc le plancher ici, côté affichage, PLUTÔT que de toucher au
// moteur : celui-ci désactive des comptes dans le pipeline de sync.
//
// ⚠️ PAS DE REPLI SUR `highWaterMark` quand `floorAmount` est absent, et ce
// délibérément : en funded le plancher est figé à initial + 100 (voir
// FUNDED_TRAILING_LOCK_OFFSET), donc `highWaterMark - maxAllowedLoss` ne le
// reconstruit pas. La branche trailing du moteur écrit TOUJOURS
// `floorAmount` : un payload sans `floorAmount` est un payload dont on ne peut
// pas reconstruire le sens — `null` (pas d'axe) bat un plancher faux.
//
// ⚠️ NE PAS CONFONDRE ce refus avec le repli de BORNE DROITE ajouté dans
// `resolveBounds` (voir `targetKind: "peak"`). Le refus ci-dessus interdit de
// CALCULER un plancher depuis `highWaterMark` : il reste entier. Le repli, lui,
// LIT `highWaterMark` tel quel comme borne droite, sans arithmétique et sans
// jamais toucher au plancher — deux choses différentes.

import { isPropFirmFundedStatus } from "./accountStatus";

/** Sous-ensemble de `BufferStatus` dont l'axe a besoin. */
export interface FloorInput {
  ruleType: "daily_loss" | "total_loss" | "trailing_drawdown" | null;
  maxAllowedLoss: number;
  floorAmount?: number;
  /**
   * Pic historique du solde (`BufferStatus.highWaterMark`, écrit par les deux
   * branches trailing de `computeBufferStatus`). Lu UNIQUEMENT comme borne
   * droite de repli sur un compte financé, jamais pour reconstruire un
   * plancher — voir le commentaire d'en-tête.
   */
  highWaterMark?: number;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * Plancher de perte du compte, en valeur absolue de solde.
 *
 * Rend `null` quand aucun plancher DURABLE n'existe : pas de règle, règle
 * purement journalière, ou valeur non exploitable.
 */
export function resolveFloor(
  buffer: FloorInput | null | undefined,
  initialBalance: number,
): number | null {
  if (!buffer || buffer.ruleType === null) return null;

  if (buffer.ruleType === "trailing_drawdown") {
    // Pas de repli si absent : voir le commentaire d'en-tête. `maxAllowedLoss`
    // ne veut pas dire la même chose dans la branche post-payout du moteur,
    // donc aucun autre champ ne permet de reconstruire le plancher sans risque
    // de mentir sur la marge de sécurité.
    return isFiniteNumber(buffer.floorAmount) ? buffer.floorAmount : null;
  }

  if (buffer.ruleType === "total_loss") {
    // maxAllowedLoss <= 0 : tier enregistré sans montant (tierRuleValidation
    // existe pour ça). Un plancher au solde de départ affirmerait une marge
    // nulle — une fausse alerte pire que pas d'axe.
    if (buffer.maxAllowedLoss <= 0) return null;
    const floor = initialBalance - buffer.maxAllowedLoss;
    return isFiniteNumber(floor) ? floor : null;
  }

  // daily_loss : plancher de la JOURNÉE, pas du compte. Hors axe.
  return null;
}

export interface BoundsInput {
  /** `challenge`, `pa`, `live`, `demo`, `inactive`. */
  status: string;
  initialBalance: number;
  /**
   * Base de l'objectif : `tier.accountSize ?? initialBalance`, exactement
   * comme `computeChallengeProgress`. NE PAS recalculer autrement — c'est ce
   * qui fait coïncider notre chiffre avec le relevé de la prop firm.
   */
  objectiveBase: number;
  buffer: FloorInput | null | undefined;
  /** `challengeProgress.targetAmount`. */
  challengeTargetAmount: number | null;
  /**
   * `payoutProgress.targetAmount`.
   *
   * ⚠️ C'est un PROFIT, pas un niveau de solde : `computePayoutProgress`
   * (propFirmEngine.ts:656-676) le compare à `currentBalance - initialBalance`.
   * La borne droite vaut donc `objectiveBase + payoutTargetAmount`, exactement
   * comme `challengeTargetAmount` ci-dessus.
   */
  payoutTargetAmount: number | null;
}

/**
 * Nature de la borne droite, pour que l'UI l'étiquette honnêtement.
 *
 * `objective` : objectif de profit d'un challenge.
 * `payout` : seuil de retrait chiffré d'un compte financé.
 * `peak` : pic historique, utilisé faute de seuil (ce n'est PAS un objectif —
 * le libeller « Cible » mentirait au trader).
 */
export type AxisTargetKind = "objective" | "payout" | "peak";

export interface AxisBounds {
  /** Plancher de perte, borne gauche. */
  floor: number;
  /** Point de départ, marqué sur l'axe. */
  start: number;
  /** Cible, borne droite. */
  target: number;
  /** Ce que REPRÉSENTE la borne droite. */
  targetKind: AxisTargetKind;
}

/**
 * Bornes de l'axe, ou `null` quand l'axe ne peut pas se dessiner.
 *
 * Le repli n'est pas un cas dégradé à contourner : afficher un axe avec une
 * borne inventée affirmerait une marge de sécurité qui n'existe pas.
 */
export function resolveBounds(input: BoundsInput): AxisBounds | null {
  const floor = resolveFloor(input.buffer, input.initialBalance);
  if (floor === null) return null;

  const start = input.objectiveBase;
  // <= 0 : palier sans taille exploitable (colonne nullable côté admin, cf.
  // TierSnapshot.accountSize). Un axe parti de 0 $ ou d'une base négative ne
  // représente aucun compte réel — mieux vaut pas d'axe qu'un axe absurde.
  if (!isFiniteNumber(start) || start <= 0) return null;

  let target: number | null;
  let targetKind: AxisTargetKind;

  // Programme live : même axe qu'un financé (plancher -> seuil de payout ou
  // pic). `resolveFloor` a déjà écarté un compte perso, qui n'a aucun plancher.
  if (isPropFirmFundedStatus(input.status, true)) {
    // LE SEUIL DE PAYOUT EST UN PROFIT, PAS UN NIVEAU DE SOLDE. Il faut donc
    // l'ajouter à `objectiveBase`, comme le fait déjà la branche challenge
    // juste en dessous. Le lire comme une borne de solde comparait un seuil de
    // 500 $ (Topstep 50k) à un plancher de 54 000 $ : `floor >= target`, et
    // l'axe refusait silencieusement de se dessiner.
    //
    // <= 0 : même piège de saisie admin que `challengeTargetAmount` — un seuil
    // nul affirmerait un retrait déjà acquis.
    const payoutTarget =
      input.payoutTargetAmount !== null && input.payoutTargetAmount > 0
        ? start + input.payoutTargetAmount
        : null;

    // PRÉCÉDENCE : le seuil de payout ne gouverne QUE s'il est strictement
    // au-dessus du plancher. Sur un trailing drawdown rentable le plancher
    // remonte avec le pic (54 299,30 pour un pic à 56 299,30) et dépasse
    // largement un seuil de retrait modeste (50 500). Dessiner un axe inversé
    // est impossible, et refuser l'axe priverait le trader financé de sa
    // distance au plancher — la seule chose qui menace réellement son compte.
    // On se replie donc sur le pic, TOUJOURS au-dessus du plancher sur un
    // trailing (`floorAmount = peak - maxDrawdown`).
    if (payoutTarget !== null && payoutTarget > floor) {
      target = payoutTarget;
      targetKind = "payout";
    } else {
      // REPLI SUR LE PIC. Certaines prop firms (Apex) ne chiffrent aucun seuil
      // de retrait : `payoutProgress.targetAmount` sort `null` et l'axe
      // disparaissait — supprimant justement ce qu'un trader financé lit en
      // premier, sa distance au plancher.
      //
      // Sur un trailing drawdown le plancher SUIT le pic à distance fixe :
      // « plancher -> pic » EST la règle qui menace le compte. Le pic n'est pas
      // une donnée inventée, `computeBufferStatus` le rend déjà dans
      // `highWaterMark` — on le LIT, on ne le calcule pas.
      //
      // Strictement au-dessus du plancher : un axe de largeur nulle ou inversée
      // ne dit rien.
      const peak = input.buffer?.highWaterMark;
      target = isFiniteNumber(peak) && peak > floor ? peak : null;
      targetKind = "peak";
    }
  } else {
    // Un challenge a TOUJOURS un objectif de profit ; s'il n'en a pas, il n'y a
    // génuinement aucun objectif à dessiner et le pic n'en tient pas lieu. Le
    // repli ci-dessus est réservé aux comptes financés.
    //
    // challengeTargetAmount <= 0 : même piège de saisie que `maxAllowedLoss`
    // pour total_loss (voir resolveFloor) — un palier enregistré sans profit
    // cible affirmerait un challenge déjà gagné. `null` plutôt qu'un axe à
    // largeur nulle.
    target =
      input.challengeTargetAmount !== null && input.challengeTargetAmount > 0
        ? start + input.challengeTargetAmount
        : null;
    targetKind = "objective";
  }

  if (target === null || !isFiniteNumber(target)) return null;

  // Palier incohérent : un axe qui va de droite à gauche ne représente rien.
  if (floor >= target) return null;

  // Cible en dessous du départ : demander au trader d'« atteindre » un point
  // situé derrière lui n'a pas de sens. Les deux branches chiffrées ajoutent
  // désormais un delta positif à `start` et ne peuvent plus l'atteindre ; il
  // reste le repli sur le pic (un pic sous le départ = compte jamais monté au
  // dessus de sa base), et on ne fait pas confiance à l'appelant ici.
  if (target < start) return null;

  // Plancher AU-DESSUS du départ : NE PAS refuser. Sur un trailing drawdown
  // rentable, `floorAmount = peak - maxDrawdown` (propFirmEngine.ts:424)
  // dépasse légitimement `objectiveBase` dès que le trader est en profit —
  // c'est l'état recherché (perte devenue impossible), pas une anomalie. Le
  // dessiner avec `start` à gauche du plancher reste honnête : la position du
  // point de départ ne ment pas, elle montre juste qu'il est maintenant
  // sécurisé. Seul `floor >= target` (deux bornes qui se croisent) est refusé
  // plus haut.

  return { floor, start, target, targetKind };
}

export interface AxisGeometry {
  /** Position du repère de départ, fraction de [0, 1] depuis la gauche. */
  startRatio: number;
  /** Position de la balance courante, fraction de [0, 1]. */
  balanceRatio: number;
  /** Début du remplissage (fraction). */
  fillStartRatio: number;
  /** Fin du remplissage (fraction). */
  fillEndRatio: number;
  /** Le compte est-il au-dessus de son point de départ ? */
  isPositive: boolean;
  /** Marge au-dessus du plancher, en monnaie. Jamais négative. */
  drawdown: number;
  /** Balance courante, en monnaie. */
  balance: number;
  /** Reste avant la cible, en monnaie. Jamais négatif. */
  remaining: number;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/**
 * Positions et valeurs de l'axe pour une balance donnée.
 *
 * Toutes les positions sont bornées à [0, 1] : une balance au-delà de la
 * cible sature à droite plutôt que de déborder du tracé.
 *
 * ⚠️ `currentBalance` non fini (NaN/undefined glissé malgré le typage) :
 * `balance` est rendu TEL QUEL, sans clamp, car cette valeur alimente un
 * `Intl.NumberFormat` côté rendu — même piège que documenté dans
 * `TradingAccountsPage.tsx` (« undefined slips past a bare inequality »).
 * On ne peut pas transformer une valeur invalide en 0 $ ici : un plancher
 * de compte à 0 mentirait autant qu'un NaN affiché. C'est à l'appelant de
 * garder `isFiniteNumber` en garde AVANT d'appeler cette fonction — même
 * garantie que `resolveBounds` impose déjà sur ses propres entrées.
 */
export function buildAxisGeometry(
  bounds: AxisBounds,
  currentBalance: number,
): AxisGeometry {
  const span = bounds.target - bounds.floor;
  const ratioOf = (value: number) => clamp01((value - bounds.floor) / span);

  const startRatio = ratioOf(bounds.start);
  const balanceRatio = ratioOf(currentBalance);
  const isPositive = currentBalance >= bounds.start;

  return {
    startRatio,
    balanceRatio,
    fillStartRatio: isPositive ? startRatio : balanceRatio,
    fillEndRatio: isPositive ? balanceRatio : startRatio,
    isPositive,
    drawdown: Math.max(0, currentBalance - bounds.floor),
    balance: currentBalance,
    remaining: Math.max(0, bounds.target - currentBalance),
  };
}
